import { useState, type CSSProperties } from 'react';
import { connectorLogoUrl, type ConnectorStatus } from '../../../lib/connectors';
import { NATIVE_CALENDAR_ICONS } from '../../../lib/native-calendar-icons';

export function ConnectorLogo({
  connector,
  name,
  size = 40,
  /** Tile fill behind the logo. Defaults to `--connector-plate`, native's `backgroundNeutral`:
   *  #F3F3F3 in light, and in dark the PAGE colour — a well punched below the card rather than a
   *  plate on top of it. The only white you then see is whatever the brand artwork itself carries
   *  (the bundled Outlook/Gmail assets are white squares; a Composio SVG is a bare glyph on
   *  transparency). The chat connect-card overrides this with a fixed white, which is native's
   *  separate `connectorBadgePlate`. */
  tileBackground = 'var(--connector-plate)',
  radius = 10,
  /** Glyph edge. Native's rule is 0.6 of the tile (`ConnectorLogo`: `glyphSize ?? size * 0.6`);
   *  the 28px compact tile pins 18 per Figma. Web previously inset the glyph by a flat 4px, i.e.
   *  0.8 of a 40px tile, so every logo sat noticeably larger in its plate than on the phones. */
  glyphSize,
}: {
  connector: ConnectorStatus;
  name: string;
  size?: number;
  tileBackground?: string;
  radius?: number;
  glyphSize?: number;
}) {
  const glyph = glyphSize ?? Math.round(size * 0.6);
  const [failed, setFailed] = useState(false);
  const tileStyle: CSSProperties = {
    width: size,
    height: size,
    flexShrink: 0,
    borderRadius: radius,
    background: tileBackground,
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  };
  if (connector.provider === 'native') {
    const icon = connector.calendarProvider ? NATIVE_CALENDAR_ICONS[connector.calendarProvider as keyof typeof NATIVE_CALENDAR_ICONS] : undefined;
    return (
      <div style={tileStyle}>
        {icon ? (
          <img src={icon} alt="" width={glyph} height={glyph} style={{ objectFit: 'contain' }} />
        ) : (
          <LogoFallback letter={name.charAt(0)} size={size} radius={radius} background={tileBackground === 'transparent' ? 'transparent' : undefined} />
        )}
      </div>
    );
  }
  const src = connector.logo || connectorLogoUrl(connector.toolkit);
  if (failed) return <LogoFallback letter={name.charAt(0)} size={size} radius={radius} background={tileBackground === 'transparent' ? 'transparent' : undefined} />;
  return (
    <div style={tileStyle}>
      <img src={src} alt="" width={glyph} height={glyph} onError={() => setFailed(true)} style={{ objectFit: 'contain' }} />
    </div>
  );
}

export function LogoFallback({
  letter,
  size = 40,
  radius = 10,
  background = 'rgba(var(--ink-rgb),0.06)',
}: {
  letter: string;
  size?: number;
  radius?: number;
  background?: string;
}) {
  return (
    <div
      aria-hidden
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: radius,
        background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: Math.round(size * 0.44),
        fontWeight: 'var(--sym-weight-semibold)',
        color: 'rgba(var(--ink-rgb),0.7)',
      }}
    >
      {(letter || '?').toUpperCase()}
    </div>
  );
}
